import { useEffect, useRef, useState } from 'react';
import { SearchPeople } from '../../api/api';
import { contactToGuest } from '../../utils/contacts';

// Type-ahead from MY OWN synced phone book (Evt_UserContacts) while typing in a
// guest's Surname / Name / Father-Husband / Mobile box. One suggestion per phone
// number (a contact with 3 numbers gives 3 lines). Picking fills all four
// fields of that guest (onPick(person, target) — target = 'head' or a family
// member's index). Keyboard: ↓ ↑ to move, Enter to pick, Esc to close.
// The other names already typed on the same row go along as `context`, so
// "Vijay" in Name with "Punjabi" in Surname searches "Punjabi Vijay" (a lone
// "Vijay" matches too many contacts and the list stops at 30). If that finds
// nothing, the box's own text is searched alone.
// Uses SearchEvt_People and reads only its `contacts` part.
export function useContactSuggest({ organiserId, userId, onPick }) {
  const [active, setActive] = useState(null);   // { target, field }
  const [text, setText] = useState('');
  const [context, setContext] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    const q = String(text || '').trim();
    const isNumber = active?.field === 'MobileNumber';
    const enough = isNumber ? q.replace(/\D/g, '').length >= 3 : q.length >= 2;
    if (!active || !enough) { setResults([]); setLoading(false); return undefined; }
    const my = ++seq.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const ctx = isNumber ? '' : String(context || '').trim();
        let r = await SearchPeople({ OrganiserId: organiserId, UserId: userId, Text: ctx ? `${ctx} ${q}` : q, AllGuests: 0 });
        if (ctx && my === seq.current && !(r?.contacts || []).length) {
          r = await SearchPeople({ OrganiserId: organiserId, UserId: userId, Text: q, AllGuests: 0 });
        }
        if (my !== seq.current) return;
        setResults((r?.contacts || []).map((c, i) => ({
          key: `${c.DisplayName}|${c.MobileNumber}|${i}`,
          ...contactToGuest(c),
          display: c.DisplayName || '',
          label: c.NumberLabel || '',
          primary: Number(c.IsPrimary) === 1,
        })));
        setHi(0);
      } catch {
        if (my === seq.current) setResults([]);
      } finally {
        if (my === seq.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [text, context, active, organiserId, userId]);

  // Call from the field's onChange (after the form state is updated).
  const typed = (target, field, value, ctx = '') => {
    setActive(a => (a && a.target === target && a.field === field ? a : { target, field }));
    setText(value);
    setContext(ctx);
  };
  const close = () => { seq.current += 1; setActive(null); setResults([]); setLoading(false); };
  const pick = (item) => { const target = active?.target; close(); if (item) onPick(item, target); };
  const isOpen = (target, field) => !!active && active.target === target && active.field === field && (loading || results.length > 0);
  const keyDown = (e) => {
    if (!active || !results.length) { if (e.key === 'Escape' && active) close(); return false; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(results.length - 1, h + 1)); return true; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); return true; }
    if (e.key === 'Enter') { e.preventDefault(); pick(results[hi]); return true; }
    if (e.key === 'Escape') { e.preventDefault(); close(); return true; }
    return false;
  };

  return { typed, close, pick, keyDown, isOpen, results, loading, hi, setHi };
}
